import { useEffect, useMemo, useState } from "react";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { PageHeader } from "@/components/dashboard/shared/PageHeader";
import { SectionCard } from "@/components/dashboard/shared/SectionCard";
import { dashboardStyles } from "@/components/dashboard/shared/dashboard-styles";
import { DashboardHead } from "@/components/SEO/DashboardHead";
import api from "@/utils/api";
import { useCurrentStore } from "@/hooks/useCurrentStore";
import { formatFullCurrency } from "@/lib/format";
import { POS_PAYMENT_METHODS } from "@/lib/permissions";
import { cn } from "@/lib/utils";
import { RiAddLine, RiDeleteBin6Line, RiSearchLine, RiSubtractLine } from "@remixicon/react";
import type { InventoryItem } from "@/components/dashboard/inventory/InventoryList";

type CartLine = InventoryItem & { quantity: number };

type InventoryResponse = {
  items: InventoryItem[];
};

export function PosPage() {
  const queryClient = useQueryClient();
  const { storeId, currentStore } = useCurrentStore();
  const currency = currentStore?.currency || "NGN";
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [cart, setCart] = useState<CartLine[]>([]);
  const [paymentMethod, setPaymentMethod] = useState("cash");
  const [customerName, setCustomerName] = useState("");
  const [customerPhone, setCustomerPhone] = useState("");
  const [customerEmail, setCustomerEmail] = useState("");
  const [note, setNote] = useState("");

  useEffect(() => {
    const timeout = window.setTimeout(() => setSearch(searchInput.trim()), 200);
    return () => window.clearTimeout(timeout);
  }, [searchInput]);

  const { data, isFetching } = useQuery({
    queryKey: ["pos-products", storeId, search],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (search) params.set("search", search);
      params.set("limit", "20");
      params.set("stockStatus", "all");
      const { data: response } = await api.get<InventoryResponse>(`/api/inventory?${params}`);
      return response;
    },
    enabled: !!storeId,
    placeholderData: keepPreviousData,
  });

  const products = data?.items || [];
  const cartTotal = useMemo(
    () => cart.reduce((sum, line) => sum + (Number(line.price) || 0) * line.quantity, 0),
    [cart],
  );

  const addToCart = (item: InventoryItem & { price?: number }) => {
    if (item.inventory <= 0) {
      toast.error("This item is out of stock");
      return;
    }
    setCart((current) => {
      const existing = current.find((line) => line.variantId === item.variantId);
      if (existing) {
        if (existing.quantity >= item.inventory) {
          toast.error("Not enough stock");
          return current;
        }
        return current.map((line) =>
          line.variantId === item.variantId ? { ...line, quantity: line.quantity + 1 } : line,
        );
      }
      return [...current, { ...item, quantity: 1 }];
    });
  };

  const updateQty = (variantId: string, quantity: number) => {
    setCart((current) =>
      current
        .map((line) => {
          if (line.variantId !== variantId) return line;
          const next = Math.min(line.inventory, Math.max(0, quantity));
          return { ...line, quantity: next };
        })
        .filter((line) => line.quantity > 0),
    );
  };

  const checkout = useMutation({
    mutationFn: async () => {
      const { data: response } = await api.post("/api/pos", {
        paymentMethod,
        note: note.trim() || undefined,
        customer:
          customerName.trim() || customerPhone.trim() || customerEmail.trim()
            ? {
                name: customerName.trim() || undefined,
                phone: customerPhone.trim() || undefined,
                email: customerEmail.trim() || undefined,
              }
            : undefined,
        items: cart.map((line) => ({
          productId: line.productId,
          variantId: line.variantId,
          quantity: line.quantity,
        })),
      });
      return response;
    },
    onSuccess: (response) => {
      toast.success(`Sale ${response.sale?.saleNumber || ""} recorded`);
      setCart([]);
      setCustomerName("");
      setCustomerPhone("");
      setCustomerEmail("");
      setNote("");
      queryClient.invalidateQueries({ queryKey: ["inventory"] });
      queryClient.invalidateQueries({ queryKey: ["inventory-movements"] });
      queryClient.invalidateQueries({ queryKey: ["pos-products"] });
      queryClient.invalidateQueries({ queryKey: ["orders"] });
    },
    onError: (error: { response?: { data?: { message?: string } } }) => {
      toast.error(error.response?.data?.message || "Could not complete sale");
    },
  });

  return (
    <div className="space-y-6">
      <DashboardHead pageName="POS Mode" />
      <PageHeader
        title="POS Mode"
        subtitle="Search a product, add it to the sale, then record payment."
      />

      <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_22rem]">
        <SectionCard>
          <div className="relative mb-4">
            <RiSearchLine className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Search products by name or SKU"
              value={searchInput}
              onChange={(event) => setSearchInput(event.target.value)}
              className={dashboardStyles.filterInput}
              autoFocus
            />
          </div>
          <div className={cn("grid gap-2 sm:grid-cols-2", isFetching && "opacity-80")}>
            {products.map((item) => {
              const outOfStock = item.inventory <= 0;
              return (
              <button
                key={item.variantId}
                type="button"
                disabled={outOfStock}
                onClick={() => addToCart(item)}
                className="flex items-center gap-3 rounded-2xl bg-muted p-3 text-left text-foreground transition-colors hover:bg-accent disabled:cursor-not-allowed disabled:opacity-60"
              >
                {item.image ? (
                  <img src={item.image} alt="" className="h-12 w-12 rounded-xl object-cover" />
                ) : (
                  <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-card text-xs font-medium text-muted-foreground">
                    SKU
                  </div>
                )}
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-semibold text-foreground">{item.name}</p>
                  <p className="truncate text-xs text-muted-foreground">
                    {item.variantLabel || item.sku || "Default"} · {item.inventory} left
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-xs font-semibold tabular-nums text-foreground">
                    {formatFullCurrency(Number(item.price) || 0, currency)}
                  </p>
                  <span
                    className={cn(
                      "mt-1 inline-flex rounded-full px-2 py-0.5 text-[11px] font-semibold",
                      outOfStock
                        ? "bg-card text-muted-foreground"
                        : "bg-primary text-primary-foreground",
                    )}
                  >
                    {outOfStock ? "Out" : "Add"}
                  </span>
                </div>
              </button>
              );
            })}
          </div>
            {products.length === 0 ? (
            <p className="py-10 text-center text-sm text-muted-foreground">
              {search ? "No matching products" : "Add products to your catalog to start taking POS sales."}
            </p>
          ) : null}
        </SectionCard>

        <SectionCard className="h-fit xl:sticky xl:top-4">
          <p className={dashboardStyles.sectionTitle}>Current sale</p>
          <div className="mt-4 space-y-3">
            {cart.length === 0 ? (
              <p className="text-sm text-muted-foreground">No items yet.</p>
            ) : (
              cart.map((line) => (
                <div key={line.variantId} className="rounded-2xl bg-muted p-3">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="truncate text-sm font-semibold">{line.name}</p>
                      <p className="text-xs text-muted-foreground">
                        {line.variantLabel || line.sku} · {formatFullCurrency((Number(line.price) || 0) * line.quantity, currency)}
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => updateQty(line.variantId, 0)}
                      className="text-muted-foreground hover:text-destructive"
                      aria-label="Remove"
                    >
                      <RiDeleteBin6Line className="h-4 w-4" />
                    </button>
                  </div>
                  <div className="mt-2 flex items-center gap-2">
                    <Button
                      type="button"
                      size="icon-xs"
                      variant="outline"
                      className="rounded-full"
                      aria-label="Decrease quantity"
                      onClick={() => updateQty(line.variantId, line.quantity - 1)}
                    >
                      <RiSubtractLine className="h-3.5 w-3.5" />
                    </Button>
                    <span className="w-8 text-center text-sm font-semibold tabular-nums text-foreground">{line.quantity}</span>
                    <Button
                      type="button"
                      size="icon-xs"
                      variant="outline"
                      className="rounded-full"
                      aria-label="Increase quantity"
                      onClick={() => updateQty(line.variantId, line.quantity + 1)}
                    >
                      <RiAddLine className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
              ))
            )}
          </div>

          <div className="mt-5 space-y-3">
            <div>
              <Label className="mb-2 block">Payment method</Label>
              <div className="flex flex-wrap gap-2">
                {POS_PAYMENT_METHODS.map((method) => {
                  const selected = paymentMethod === method.id;
                  return (
                  <Button
                    key={method.id}
                    type="button"
                    variant="ghost"
                    aria-pressed={selected}
                    onClick={() => setPaymentMethod(method.id)}
                    className={cn(
                      "h-9 rounded-full px-3.5 text-xs font-semibold shadow-none",
                      selected
                        ? "bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground"
                        : "bg-muted text-foreground hover:bg-accent hover:text-foreground",
                    )}
                  >
                    {method.label}
                  </Button>
                  );
                })}
              </div>
            </div>

            <div className="space-y-2">
              <Label>Customer (optional)</Label>
              <Input
                placeholder="Name"
                value={customerName}
                onChange={(event) => setCustomerName(event.target.value)}
                className="rounded-full"
              />
              <Input
                placeholder="Phone"
                value={customerPhone}
                onChange={(event) => setCustomerPhone(event.target.value)}
                className="rounded-full"
              />
              <Input
                placeholder="Email"
                value={customerEmail}
                onChange={(event) => setCustomerEmail(event.target.value)}
                className="rounded-full"
              />
            </div>
            <Textarea
              placeholder="Note (optional)"
              value={note}
              onChange={(event) => setNote(event.target.value)}
            />
          </div>

          <div className="mt-5 flex items-center justify-between">
            <span className="text-sm text-muted-foreground">
              {cart.length === 1 ? "1 item" : `${cart.length} items`}
            </span>
            <span className="font-heading text-xl font-bold tabular-nums">
              {formatFullCurrency(cartTotal, currency)}
            </span>
          </div>
          <Button
            className="mt-4 h-11 w-full rounded-full font-semibold"
            disabled={cart.length === 0 || checkout.isPending}
            onClick={() => checkout.mutate()}
          >
            {checkout.isPending ? "Recording..." : "Complete sale"}
          </Button>
        </SectionCard>
      </div>
    </div>
  );
}
